import { z } from 'zod';
import { uuid, paginationQuery, isoDatetime } from './common.js';

// ─── Enums ────────────────────────────────────────────────────────────────
export const EmergencyKind = {
  MEDICAL: 'MEDICAL',
  FIRE: 'FIRE',
  SECURITY: 'SECURITY',
  OTHER: 'OTHER',
} as const;
export type EmergencyKind = (typeof EmergencyKind)[keyof typeof EmergencyKind];

export const EmergencyStatus = {
  ACTIVE: 'ACTIVE',
  RESOLVED: 'RESOLVED',
  CANCELLED: 'CANCELLED',
} as const;
export type EmergencyStatus =
  (typeof EmergencyStatus)[keyof typeof EmergencyStatus];

const KINDS = [
  EmergencyKind.MEDICAL,
  EmergencyKind.FIRE,
  EmergencyKind.SECURITY,
  EmergencyKind.OTHER,
] as const;
const STATUSES = [
  EmergencyStatus.ACTIVE,
  EmergencyStatus.RESOLVED,
  EmergencyStatus.CANCELLED,
] as const;

const lat = z.string().regex(/^-?\d+(\.\d+)?$/, 'Expected decimal').max(20);
const lng = z.string().regex(/^-?\d+(\.\d+)?$/, 'Expected decimal').max(20);

// ─── Public shape ─────────────────────────────────────────────────────────
export const publicEmergencyAlert = z.object({
  id: uuid,
  cluster_id: uuid,
  unit_id: uuid,
  raised_by_user_id: uuid,
  raised_by_name: z.string().optional(),
  unit_block: z.string().optional(),
  unit_number: z.string().optional(),
  kind: z.enum(KINDS),
  note: z.string().nullable(),
  lat: z.string().nullable(),
  lng: z.string().nullable(),
  notify_neighbors: z.boolean(),
  status: z.enum(STATUSES),
  resolved_at: isoDatetime.nullable(),
  resolved_by_user_id: uuid.nullable(),
  resolved_by_name: z.string().optional(),
  resolved_notes: z.string().nullable(),
  cancelled_at: isoDatetime.nullable(),
  cancelled_reason: z.string().nullable(),
  created_at: isoDatetime,
  updated_at: isoDatetime,
});
export type PublicEmergencyAlert = z.infer<typeof publicEmergencyAlert>;

// ─── Resident requests ────────────────────────────────────────────────────
export const raiseEmergencyRequest = z.object({
  kind: z.enum(KINDS).default(EmergencyKind.OTHER),
  note: z.string().max(500).trim().optional(),
  lat: lat.optional(),
  lng: lng.optional(),
  notify_neighbors: z.boolean().default(false),
});
export type RaiseEmergencyRequest = z.infer<typeof raiseEmergencyRequest>;

export const cancelEmergencyRequest = z.object({
  reason: z.string().max(200).optional(),
});

// ─── Security/staff requests ──────────────────────────────────────────────
export const resolveEmergencyRequest = z.object({
  notes: z.string().min(1).max(500).trim(),
});
export type ResolveEmergencyRequest = z.infer<typeof resolveEmergencyRequest>;

// ─── List queries ─────────────────────────────────────────────────────────
export const listEmergencyQuery = paginationQuery.extend({
  status: z.enum(STATUSES).optional(),
});
export type ListEmergencyQuery = z.infer<typeof listEmergencyQuery>;
